import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import mapboxgl, { type FillPaint } from "mapbox-gl";
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Bar,
  BarChart,
} from "recharts";
import Map, { Source, Layer } from "@components/Map";
import Text from "@components/Text";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import {
  getTracks,
  getDeviceDetail,
  AdminTrackPoint,
  type AdminDeviceDetail,
} from "@services/adminService";
import { deviceStatisticsFromAdminTracks } from "@services/devicesService";
import { sensorFeatures, speedFeatures } from "@screens/TracksScreen/utils";
import { useLang } from "@src/hooks/useLang";
import {
  DateRow,
  HintText,
  LayerSelect,
  LoadingWrap,
  MapLayerToolbar,
  MapWrap,
  MetricsHeader,
  MetricsShell,
  MetricsSub,
  MetricsTitle,
  Panel,
  PanelTitle,
  PanelTitleRow,
  ResetZoomBtn,
  TimeRangeDropdown,
  ToggleLabel,
  ToggleSwatch,
  Toolbar,
  defaultDateRange,
  fmtLocalDatetime,
  mean,
  percentileDomain,
  stdDev,
  toISO,
  useChartZoom,
  tooltipStyle,
} from "@components/DeviceMetrics";
import { cameraDumpThreshold } from "@src/utils/cameraDump";

// ── Time Range Presets ─────────────────────────────────────



// ── Styled Components ──────────────────────────────────────


const DateInput = styled.input`
  height: 36px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
`;

const LoadButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;





























const CameraStat = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #c3cad2;
  b {
    color: #eef2f6;
    margin-left: 2px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "SF Mono", "Consolas", monospace;
  font-size: 11px;
  margin-top: 8px;
  color: #c7d0d9;
  th {
    text-align: left;
    color: #73bf69;
    font-weight: 600;
    padding: 4px 8px 4px 0;
    border-bottom: 1px solid #2a2e37;
  }
  td {
    padding: 4px 8px 4px 0;
    border-bottom: 1px solid #1e2229;
  }
`;


// ── Helpers ────────────────────────────────────────────────

interface Props {
  esn: string;
}




type ChartRow = {
  time: number;
  value: number;
  freq: number;
  speed: number;
  satQty: number;
  gpsHdop: number;
  /** Potato-camera per-point counts (null when the point carried no camera data) */
  cvPotato: number | null;
  cvStone: number | null;
  /** Fuel sensor input, mV (null unless the device has the fuel-sensor switch on) */
  fuelMv: number | null;
};





const ADMIN_SOURCE_SENSOR = "admin-metrics-sensor";
const ADMIN_SOURCE_SPEED = "admin-metrics-speed";

/** Same `fill-color` as TracksScreen sensor layer (`Sensor` vs `Speed` dropdown). */
const TRACKS_SENSOR_FILL_PAINT = {
  "fill-outline-color": "rgba(0, 0, 0, 0)",
  "fill-color": [
    "interpolate",
    ["linear"],
    ["get", "sensor"],
    0.0, "rgb(165.0, 0.0, 38.0)",
    5.0, "rgb(215.0, 48.0, 39.0)",
    10.0, "rgb(244.0, 109.0, 67.0)",
    15.0, "rgb(253.0, 174.0, 97.0)",
    20.0, "rgb(254.0, 224.0, 139.0)",
    35.0, "rgb(255.0, 255.0, 191.0)",
    45.0, "rgb(217.0, 239.0, 139.0)",
    55.0, "rgb(166.0, 217.0, 106.0)",
    62.5, "rgb(102.0, 189.0, 99.0)",
    80.0, "rgb(26.0, 152.0, 80.0)",
    90.0, "rgb(0.0, 104.0, 55.0)",
    99.0, "rgb(37.0, 37.0, 37.0)",
  ],
} as FillPaint;

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] as never[] };

// Flatten every coordinate out of a GeoJSON FeatureCollection (Polygon or
// LineString) so the map can be fit to the geometry actually rendered.
function collectFeatureCoords(fc: {
  features: Array<{ geometry?: { type: string; coordinates: any } }>;
}): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const f of fc.features ?? []) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === "Polygon") {
      for (const ring of g.coordinates as number[][][])
        for (const c of ring) out.push([c[0], c[1]]);
    } else if (g.type === "LineString") {
      for (const c of g.coordinates as number[][]) out.push([c[0], c[1]]);
    }
  }
  return out;
}

// Robust fallback bounds from raw track points: drop (0,0) fixes and any point
// far from the median center (a stray GPS jump otherwise zooms the map out to a
// whole region). Threshold is the larger of 3× the 95th-percentile distance and
// a ~55km hard cap, which keeps the real track while rejecting outliers.
function robustTrackCoords(
  tracks: Array<{ longitude: number; latitude: number }>,
): Array<[number, number]> {
  const valid = tracks
    .filter((t) => !(Math.abs(t.longitude) < 0.001 && Math.abs(t.latitude) < 0.001))
    .map((t) => [t.longitude, t.latitude] as [number, number]);
  if (valid.length <= 2) return valid;

  const median = (arr: number[]) => {
    const s = [...arr].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
  };
  const cx = median(valid.map((c) => c[0]));
  const cy = median(valid.map((c) => c[1]));

  const dists = valid.map((c) => Math.hypot(c[0] - cx, c[1] - cy));
  const sorted = [...dists].sort((a, b) => a - b);
  const p95 = sorted[Math.floor(sorted.length * 0.95)];
  const cap = Math.min(p95 * 3, 0.5); // adaptive spread, hard-capped at ~55km
  return valid.filter((_, i) => dists[i] <= cap);
}



// ── Main Component ─────────────────────────────────────────

const DeviceMetricsSubTab: React.FC<Props> = ({ esn }) => {
  const lang = useLang();
  const defaults = defaultDateRange();
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [selectedPresetMs, setSelectedPresetMs] = useState<number | null>(24 * 60 * 60 * 1000);
  const [trackData, setTrackData] = useState<AdminTrackPoint[]>([]);
  const [deviceDetail, setDeviceDetail] = useState<AdminDeviceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  /** Matches Tracks map: sensor polygons default, speed lines optional. */
  const [mapLayerMode, setMapLayerMode] = useState<"sensor" | "speed">("sensor");
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const [showSensor, setShowSensor] = useState(true);
  const [showFrequency, setShowFrequency] = useState(true);
  const [showSatHdop, setShowSatHdop] = useState(true);

  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);

  const handleSelectPreset = useCallback((ms: number) => {
    const now = new Date();
    const ago = new Date(now.getTime() - ms);
    setFrom(fmtLocalDatetime(ago));
    setTo(fmtLocalDatetime(now));
    setSelectedPresetMs(ms);
  }, []);

  const handleApplyAbsolute = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
    setSelectedPresetMs(null);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fromISO = toISO(from);
      const toISO_ = toISO(to);
      const [tracksRes, detail] = await Promise.all([
        getTracks({ esn, from: fromISO, to: toISO_ }),
        getDeviceDetail(esn).catch(() => null),
      ]);
      setTrackData(tracksRes.tracks);
      setDeviceDetail(detail);
      setZoomDomain(null);
    } catch (e) {
      console.error("Failed to fetch metrics:", e);
    } finally {
      setLoading(false);
    }
  }, [esn, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData: ChartRow[] = useMemo(() => {
    const all = trackData
      .map((t) => {
        const timeSec = new Date(t.time).getTime() / 1000;
        if (!Number.isFinite(timeSec)) return null;
        return {
          time: timeSec,
          value: typeof t.value === "number" ? t.value : 0,
          freq: typeof t.freq === "number" ? t.freq : 0,
          speed: typeof t.speed === "number" ? t.speed : 0,
          satQty: typeof t.satQty === "number" ? t.satQty : 0,
          gpsHdop: typeof t.gpsHdop === "number" ? t.gpsHdop : 0,
          cvPotato: typeof t.cvPotatoCount === "number" ? t.cvPotatoCount : null,
          cvStone: typeof t.cvStoneCount === "number" ? t.cvStoneCount : null,
          fuelMv: typeof t.fuelMv === "number" ? t.fuelMv : null,
        };
      })
      .filter((x): x is ChartRow => x != null)
      .sort((a, b) => a.time - b.time);

    // Downsample if too many points — SVG paths become too heavy above ~3000 pts.
    // Use LTTB-like min/max preserving: keep every Nth point but always keep local
    // min and max within each bucket to preserve peaks and valleys.
    const MAX_CHART_POINTS = 3000;
    if (all.length <= MAX_CHART_POINTS) return all;

    const step = all.length / MAX_CHART_POINTS;
    const result: ChartRow[] = [all[0]]; // always keep first
    for (let i = 0; i < MAX_CHART_POINTS - 2; i++) {
      const bucketStart = Math.floor(i * step) + 1;
      const bucketEnd = Math.floor((i + 1) * step) + 1;
      if (bucketStart >= all.length) break;
      const end = Math.min(bucketEnd, all.length);
      // Find min and max value points in this bucket
      let minIdx = bucketStart, maxIdx = bucketStart;
      for (let j = bucketStart + 1; j < end; j++) {
        if (all[j].value < all[minIdx].value) minIdx = j;
        if (all[j].value > all[maxIdx].value) maxIdx = j;
      }
      // Add them in time order (preserves shape)
      if (minIdx === maxIdx) {
        result.push(all[minIdx]);
      } else if (minIdx < maxIdx) {
        result.push(all[minIdx], all[maxIdx]);
      } else {
        result.push(all[maxIdx], all[minIdx]);
      }
    }
    result.push(all[all.length - 1]); // always keep last
    console.log(`[Metrics] Downsampled chart data: ${all.length} → ${result.length} points`);
    return result;
  }, [trackData]);

  const rangeFrom = useMemo(() => new Date(from), [from]);
  const rangeTo = useMemo(() => new Date(to), [to]);

  const statistics = useMemo(
    () => deviceStatisticsFromAdminTracks(trackData),
    [trackData],
  );

  const reaperWidthM = deviceDetail?.device.width ?? 6;
  const delaySeconds = (deviceDetail?.device.delay ?? 16000) / 1000;

  const sensorGeo = useMemo(
    () => sensorFeatures(statistics, reaperWidthM, delaySeconds, rangeFrom, rangeTo),
    [statistics, reaperWidthM, delaySeconds, rangeFrom, rangeTo],
  );

  const speedGeo = useMemo(
    () => speedFeatures(statistics, rangeFrom, rangeTo),
    [statistics, rangeFrom, rangeTo],
  );

  const fullTimeRange = useMemo(() => {
    if (chartData.length >= 2) {
      const min = chartData[0].time;
      const max = chartData[chartData.length - 1].time;
      const pad = Math.max((max - min) * 0.02, 1);
      return { min: min - pad, max: max + pad };
    }
    if (chartData.length === 1) {
      return { min: chartData[0].time - 1, max: chartData[0].time + 1 };
    }
    return null;
  }, [chartData]);

  const xDomain = useMemo((): [number, number] | undefined => {
    if (!fullTimeRange) return undefined;
    if (zoomDomain) {
      const pad = Math.max((zoomDomain[1] - zoomDomain[0]) * 0.01, 0.5);
      return [zoomDomain[0] - pad, zoomDomain[1] + pad];
    }
    return [fullTimeRange.min, fullTimeRange.max];
  }, [fullTimeRange, zoomDomain]);

  /** Points shown in charts — zoom by filtering data, not by XAxis `domain`, so Recharts `activeTooltipIndex` matches row indices. */
  const displayData = useMemo(() => {
    if (chartData.length === 0) return [];
    if (!zoomDomain) return chartData;
    const lo = Math.min(zoomDomain[0], zoomDomain[1]);
    const hi = Math.max(zoomDomain[0], zoomDomain[1]);
    return chartData.filter((p) => p.time >= lo && p.time <= hi);
  }, [chartData, zoomDomain]);

  /** Percentile-based Y-axis domains — avoids outlier values stretching the scale. */
  const yDomains = useMemo(() => {
    if (displayData.length === 0) return { sensor: [0, 1] as [number, number], freq: [0, 1] as [number, number], speed: [0, 1] as [number, number] };
    return {
      sensor: percentileDomain(displayData.map((d) => d.value)),
      freq: percentileDomain(displayData.map((d) => d.freq)),
      speed: percentileDomain(displayData.map((d) => d.speed)),
    };
  }, [displayData]);

  /** Fuel sensor (mV): shown only when the window carries readings. */
  const fuelValues = useMemo(
    () => displayData.map((d) => d.fuelMv).filter((v): v is number => v != null),
    [displayData],
  );
  const hasFuel = useMemo(() => chartData.some((d) => d.fuelMv != null), [chartData]);
  const fuelDomain = useMemo(() => percentileDomain(fuelValues), [fuelValues]);

  /**
   * Potato-camera per-point stats over the full (non-downsampled) range. Counts
   * are per-interval ("since the previous point"), not cumulative — we surface
   * per-point values (avg / peak), not a running total.
   *
   * Some firmware emits a one-off cumulative/lifetime total in the first frame
   * after the camera boots; that single point is orders of magnitude larger than
   * a real per-interval detection and would swamp every per-point view. We reject
   * it with a robust median threshold and report how many were skipped.
   */
  const cameraStats = useMemo(() => {
    const cvPts = trackData.filter(
      (t) => t.cvPotatoCount != null || t.cvStoneCount != null,
    );
    const dumpThreshold = cameraDumpThreshold(cvPts.map((t) => t.cvPotatoCount ?? 0));

    let potato = 0;
    let stone = 0;
    let pts = 0;
    let maxPotato = 0;
    let maxStone = 0;
    let dumps = 0;
    const hist = new Array(8).fill(0) as number[];
    for (const t of cvPts) {
      const p = t.cvPotatoCount ?? 0;
      const s = t.cvStoneCount ?? 0;
      if (p > dumpThreshold) {
        dumps += 1;
        continue; // cumulative/lifetime dump — not a per-point reading
      }
      pts += 1;
      potato += p;
      stone += s;
      if (p > maxPotato) maxPotato = p;
      if (s > maxStone) maxStone = s;
      if (Array.isArray(t.cvSizeHist)) {
        for (let i = 0; i < Math.min(8, t.cvSizeHist.length); i++) {
          hist[i] += t.cvSizeHist[i] ?? 0;
        }
      }
    }
    return {
      pts,
      dumps,
      dumpThreshold,
      maxPotato,
      maxStone,
      avgPotato: pts ? +(potato / pts).toFixed(1) : 0,
      avgStone: pts ? +(stone / pts).toFixed(1) : 0,
      hist,
      hasCamera: cvPts.length > 0,
    };
  }, [trackData]);

  const cameraHistData = useMemo(
    () => cameraStats.hist.map((count, i) => ({ bucket: `#${i}`, count })),
    [cameraStats],
  );

  /** Per-point Y-axis domain — percentile-capped so a lone dump can't flatten it. */
  const cameraDomain = useMemo(
    () =>
      percentileDomain(
        displayData
          .map((d) => d.cvPotato)
          .filter((v): v is number => v != null && v <= cameraStats.dumpThreshold),
      ),
    [displayData, cameraStats.dumpThreshold],
  );

  /** Camera series with cumulative dumps nulled so the per-point line stays clean. */
  const cameraSeries = useMemo(
    () =>
      displayData.map((d) => ({
        time: d.time,
        cvPotato:
          d.cvPotato != null && d.cvPotato <= cameraStats.dumpThreshold
            ? d.cvPotato
            : null,
        cvStone:
          d.cvStone != null && d.cvStone <= cameraStats.dumpThreshold
            ? d.cvStone
            : null,
      })),
    [displayData, cameraStats.dumpThreshold],
  );

  const statsRows = useMemo(() => {
    const rows: Array<{
      name: string;
      color: string;
      min: string;
      max: string;
      mean: string;
      last: string;
      std: string;
    }> = [];
    const add = (
      name: string,
      color: string,
      vals: number[],
    ) => {
      if (vals.length === 0) {
        rows.push({
          name,
          color,
          min: "—",
          max: "—",
          mean: "—",
          last: "—",
          std: "—",
        });
        return;
      }
      const last = vals[vals.length - 1];
      rows.push({
        name,
        color,
        min: vals.reduce((a, b) => Math.min(a, b)).toFixed(1),
        max: vals.reduce((a, b) => Math.max(a, b)).toFixed(1),
        mean: mean(vals).toFixed(1),
        last: last.toFixed(1),
        std: stdDev(vals).toFixed(2),
      });
    };

    add("Sensor", "#8CC152", displayData.map((p) => p.value));
    add("Frequency", "#F6BB42", displayData.map((p) => p.freq));
    add("Speed", "#73BF69", displayData.map((p) => p.speed));
    add("Satellites", "#8CC152", displayData.map((p) => p.satQty));
    add("GPS HDOP", "#F6BB42", displayData.map((p) => p.gpsHdop));
    if (hasFuel) add("Fuel sensor (mV)", "#F59E0B", fuelValues);
    return rows;
  }, [displayData, hasFuel, fuelValues]);

  const fitMapToTrack = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Prefer the geometry actually drawn on the map. sensorFeatures/speedFeatures
    // already drop GPS outliers, so these coords are the trustworthy extent.
    let coords = collectFeatureCoords(sensorGeo);
    if (coords.length === 0) coords = collectFeatureCoords(speedGeo);

    // Fallback: raw track points. The map geometry can be empty (e.g. trackShift
    // produced no points) while raw GPS still has a stray far-away fix that, if
    // included, zooms the map out to a whole region. Reject those outliers here.
    if (coords.length === 0) coords = robustTrackCoords(trackData);

    if (coords.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds(coords[0], coords[0]);
    for (const c of coords) bounds.extend(c);
    // maxZoom keeps a tiny/stationary track from zooming in to street level.
    map.fitBounds(bounds, { padding: 40, maxZoom: 16, duration: 500 });
  }, [sensorGeo, speedGeo, trackData]);

  // Update map data imperatively (same approach as user-facing TracksScreen)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const sensorSource = map.getSource(ADMIN_SOURCE_SENSOR) as mapboxgl.GeoJSONSource | undefined;
    if (sensorSource) {
      sensorSource.setData(sensorGeo.features.length ? sensorGeo as any : EMPTY_FC);
    }

    const speedSource = map.getSource(ADMIN_SOURCE_SPEED) as mapboxgl.GeoJSONSource | undefined;
    if (speedSource) {
      speedSource.setData(speedGeo.features.length ? speedGeo as any : EMPTY_FC);
    }

    fitMapToTrack();
  }, [sensorGeo, speedGeo, fitMapToTrack]);

  const formatTime = (unix: number) => {
    const d = new Date(unix * 1000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  // Axis ticks stay HH:mm (they'd collide otherwise), so the tooltip carries the
  // date — without it a multi-day range gives no way to tell which day a point is on.
  const formatTooltipLabel = (unix: number) => {
    const d = new Date(unix * 1000);
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return `${date}, ${time}`;
  };

  // Selection drives a client-side domain here; the customer page refetches at
  // a finer bucket instead. Same gesture, different consequence — which is why
  // the hook takes a callback rather than owning the zoom state.
  const { handlers: brushHandlers, selection: brushSelection } = useChartZoom(
    displayData,
    useCallback((t1: number, t2: number) => setZoomDomain([t1, t2]), [])
  );

  return (
    <div>
      <DateRow>
        <DateInput
          type="datetime-local"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setSelectedPresetMs(null);
          }}
        />
        <Text size={13} color="#899093">
          to
        </Text>
        <DateInput
          type="datetime-local"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setSelectedPresetMs(null);
          }}
        />
        <LoadButton kind="outline" onClick={fetchData}>
          Load
        </LoadButton>
        {loading && <Spinner />}
        <TimeRangeDropdown
          selectedMs={selectedPresetMs}
          from={from}
          to={to}
          onSelectPreset={handleSelectPreset}
          onApplyAbsolute={handleApplyAbsolute}
        />
      </DateRow>

      <MapWrap>
        {trackData.length > 0 && (
          <MapLayerToolbar>
            <Text size={13} color="#494c5b">
              Map
            </Text>
            <LayerSelect
              value={mapLayerMode}
              onChange={(e) =>
                setMapLayerMode(e.target.value as "sensor" | "speed")
              }
              aria-label="Track layer"
            >
              <option value="sensor">
                {lang.tracks.sensorLayerTitle ?? "Sensor"}
              </option>
              <option value="speed">
                {lang.tracks.speedLayerTitle ?? "Speed"}
              </option>
            </LayerSelect>
          </MapLayerToolbar>
        )}
        <Map
          onLoad={(e) => {
            mapRef.current = e.target;
            // Once the map style is loaded, set data and fit bounds
            setTimeout(() => {
              const map = e.target;
              const sensorSource = map.getSource(ADMIN_SOURCE_SENSOR) as mapboxgl.GeoJSONSource | undefined;
              if (sensorSource && sensorGeo.features.length) {
                sensorSource.setData(sensorGeo as any);
              }
              const speedSource = map.getSource(ADMIN_SOURCE_SPEED) as mapboxgl.GeoJSONSource | undefined;
              if (speedSource && speedGeo.features.length) {
                speedSource.setData(speedGeo as any);
              }
              fitMapToTrack();
            }, 100);
          }}
          style={{ width: "100%", height: "100%" }}
        >
          <Source
            id={ADMIN_SOURCE_SENSOR}
            type="geojson"
            data={EMPTY_FC}
          >
            <Layer
              id={`${ADMIN_SOURCE_SENSOR}-layer`}
              source={ADMIN_SOURCE_SENSOR}
              type="fill"
              layout={{
                visibility: mapLayerMode === "sensor" ? "visible" : "none",
              }}
              paint={TRACKS_SENSOR_FILL_PAINT}
            />
          </Source>
          <Source
            id={ADMIN_SOURCE_SPEED}
            type="geojson"
            data={EMPTY_FC}
          >
            <Layer
              id={`${ADMIN_SOURCE_SPEED}-layer`}
              source={ADMIN_SOURCE_SPEED}
              type="line"
              layout={{
                "line-cap": "round",
                "line-join": "round",
                visibility: mapLayerMode === "speed" ? "visible" : "none",
              }}
              paint={{
                "line-color": ["get", "line-color"],
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  5,
                  1,
                  14,
                  5,
                  20,
                  10,
                ],
              }}
            />
          </Source>
        </Map>
      </MapWrap>

      {loading ? (
        <LoadingWrap>
          <Spinner />
        </LoadingWrap>
      ) : trackData.length === 0 ? (
        <Text size={13} color="#899093" style={{ padding: "24px 0" }}>
          No track data for selected period.
        </Text>
      ) : (
        <MetricsShell>
          <MetricsHeader>
            <MetricsTitle>Metrics</MetricsTitle>
            <MetricsSub>
              Sensor, frequency, speed, and GNSS quality — drag on any chart to
              zoom the time range (same range applies to all panels). The map
              matches Tracks: Sensor shows wide strips colored by normalized
              sensor (device settings); Speed shows speed-colored lines.
            </MetricsSub>
          </MetricsHeader>

          {chartData.length === 0 ? (
            <Text size={13} color="#899093" style={{ padding: "24px 0" }}>
              No valid time-series data for charts.
            </Text>
          ) : (
            <>
              <Panel>
                <PanelTitleRow>
                  <PanelTitle>Sensor &amp; frequency</PanelTitle>
                  <Toolbar>
                    <ToggleLabel>
                      <input
                        type="checkbox"
                        checked={showSensor}
                        onChange={(e) => setShowSensor(e.target.checked)}
                      />
                      <ToggleSwatch color="#8CC152" />
                      Sensor
                    </ToggleLabel>
                    <ToggleLabel>
                      <input
                        type="checkbox"
                        checked={showFrequency}
                        onChange={(e) => setShowFrequency(e.target.checked)}
                      />
                      <ToggleSwatch color="#F6BB42" />
                      Frequency
                    </ToggleLabel>
                    <ResetZoomBtn
                      type="button"
                      onClick={() => setZoomDomain(null)}
                    >
                      Reset zoom
                    </ResetZoomBtn>
                    <HintText>Drag on plot to zoom time</HintText>
                  </Toolbar>
                </PanelTitleRow>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={displayData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    syncId="admin-metrics"
                    {...brushHandlers}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                    <XAxis
                      dataKey="time"
                      type="number"
                      scale="linear"
                      domain={xDomain}
                      tickFormatter={formatTime}
                      stroke="#8e9ba8"
                      tick={{ fill: "#8e9ba8", fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="sensor"
                      orientation="left"
                      stroke="#8CC152"
                      tick={{ fill: "#8CC152", fontSize: 11 }}
                      domain={yDomains.sensor}
                      allowDataOverflow
                    />
                    <YAxis
                      yAxisId="freq"
                      orientation="right"
                      stroke="#F6BB42"
                      tick={{ fill: "#F6BB42", fontSize: 11 }}
                      domain={yDomains.freq}
                      allowDataOverflow
                    />
                    <Tooltip
                      shared
                      contentStyle={tooltipStyle}
                      labelFormatter={formatTooltipLabel}
                    />
                    {brushSelection()}
                    <Line
                      yAxisId="sensor"
                      type="linear"
                      dataKey="value"
                      stroke="#8CC152"
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      name="Sensor"
                      connectNulls
                      hide={!showSensor}
                    />
                    <Line
                      yAxisId="freq"
                      type="linear"
                      dataKey="freq"
                      stroke="#F6BB42"
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      name="Frequency"
                      connectNulls
                      hide={!showFrequency}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Panel>

              <Panel>
                <PanelTitleRow>
                  <PanelTitle>Speed</PanelTitle>
                  <HintText>km/h</HintText>
                </PanelTitleRow>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart
                    data={displayData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    syncId="admin-metrics"
                    {...brushHandlers}
                  >
                    <defs>
                      <linearGradient id="admin_speed_fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#73BF69" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#73BF69" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                    <XAxis
                      dataKey="time"
                      type="number"
                      scale="linear"
                      domain={xDomain}
                      tickFormatter={formatTime}
                      stroke="#8e9ba8"
                      tick={{ fill: "#8e9ba8", fontSize: 11 }}
                    />
                    <YAxis
                      dataKey="speed"
                      stroke="#73BF69"
                      tick={{ fill: "#73BF69", fontSize: 11 }}
                      unit=" km/h"
                      domain={yDomains.speed}
                      allowDataOverflow
                    />
                    <Tooltip
                      shared
                      contentStyle={tooltipStyle}
                      labelFormatter={formatTooltipLabel}
                      formatter={(v: number) => [`${v.toFixed(1)} km/h`, "Speed"]}
                    />
                    {brushSelection()}
                    <Area
                      type="linear"
                      dataKey="speed"
                      stroke="#73BF69"
                      strokeWidth={1.5}
                      fill="url(#admin_speed_fill)"
                      isAnimationActive={false}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>

              {hasFuel && (
                <Panel>
                  <PanelTitleRow>
                    <PanelTitle>Fuel sensor</PanelTitle>
                    <HintText>mV, analog input (uncalibrated)</HintText>
                  </PanelTitleRow>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart
                      data={displayData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                      syncId="admin-metrics"
                      {...brushHandlers}
                    >
                      <defs>
                        <linearGradient id="admin_fuel_fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="linear"
                        domain={xDomain}
                        tickFormatter={formatTime}
                        stroke="#8e9ba8"
                        tick={{ fill: "#8e9ba8", fontSize: 11 }}
                      />
                      <YAxis
                        dataKey="fuelMv"
                        stroke="#F59E0B"
                        tick={{ fill: "#F59E0B", fontSize: 11 }}
                        unit=" mV"
                        width={72}
                        domain={fuelDomain}
                        allowDataOverflow
                      />
                      <Tooltip
                        shared
                        contentStyle={tooltipStyle}
                        labelFormatter={formatTooltipLabel}
                        formatter={(v: number) => [`${Math.round(v)} mV`, "Fuel sensor"]}
                      />
                      {brushSelection()}
                      <Area
                        type="linear"
                        dataKey="fuelMv"
                        stroke="#F59E0B"
                        strokeWidth={1.5}
                        fill="url(#admin_fuel_fill)"
                        connectNulls={false}
                        isAnimationActive={false}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Panel>
              )}

              <Panel>
                <PanelTitleRow>
                  <PanelTitle>Satellites &amp; HDOP</PanelTitle>
                  <Toolbar>
                    <ToggleLabel>
                      <input
                        type="checkbox"
                        checked={showSatHdop}
                        onChange={(e) => setShowSatHdop(e.target.checked)}
                      />
                      <ToggleSwatch color="#F6BB42" />
                      GPS HDOP
                    </ToggleLabel>
                  </Toolbar>
                </PanelTitleRow>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={displayData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    syncId="admin-metrics"
                    {...brushHandlers}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                    <XAxis
                      dataKey="time"
                      type="number"
                      scale="linear"
                      domain={xDomain}
                      tickFormatter={formatTime}
                      stroke="#8e9ba8"
                      tick={{ fill: "#8e9ba8", fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="sat"
                      orientation="left"
                      stroke="#8CC152"
                      tick={{ fill: "#8CC152", fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="hdop"
                      orientation="right"
                      stroke="#F6BB42"
                      tick={{ fill: "#F6BB42", fontSize: 11 }}
                    />
                    <Tooltip
                      shared
                      contentStyle={tooltipStyle}
                      labelFormatter={formatTooltipLabel}
                    />
                    {brushSelection()}
                    <Line
                      yAxisId="sat"
                      type="linear"
                      dataKey="satQty"
                      stroke="#8CC152"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      name="Satellites"
                      connectNulls
                    />
                    <Line
                      yAxisId="hdop"
                      type="linear"
                      dataKey="gpsHdop"
                      stroke="#F6BB42"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      name="GPS HDOP"
                      connectNulls
                      hide={!showSatHdop}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Panel>

              {cameraStats.hasCamera && (
                <Panel>
                  <PanelTitleRow>
                    <PanelTitle>Potato camera — per point</PanelTitle>
                    <Toolbar>
                      <CameraStat>
                        <ToggleSwatch color="#8CC152" /> Potato&nbsp;
                        <b>avg {cameraStats.avgPotato}</b>&nbsp;· peak {cameraStats.maxPotato}
                      </CameraStat>
                      <CameraStat>
                        <ToggleSwatch color="#E9573F" /> Stones&nbsp;
                        <b>avg {cameraStats.avgStone}</b>&nbsp;· peak {cameraStats.maxStone}
                      </CameraStat>
                      <HintText>
                        {cameraStats.pts} points/interval
                        {cameraStats.dumps > 0
                          ? ` · ${cameraStats.dumps} cumulative dump skipped`
                          : ""}
                      </HintText>
                    </Toolbar>
                  </PanelTitleRow>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={cameraSeries}
                      margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                      syncId="admin-metrics"
                      {...brushHandlers}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="linear"
                        domain={xDomain}
                        tickFormatter={formatTime}
                        stroke="#8e9ba8"
                        tick={{ fill: "#8e9ba8", fontSize: 11 }}
                      />
                      <YAxis
                        stroke="#8CC152"
                        tick={{ fill: "#8CC152", fontSize: 11 }}
                        allowDecimals={false}
                        domain={cameraDomain}
                        allowDataOverflow
                      />
                      <Tooltip
                        shared
                        contentStyle={tooltipStyle}
                        labelFormatter={formatTooltipLabel}
                      />
                      {brushSelection()}
                      <Line
                        type="linear"
                        dataKey="cvPotato"
                        stroke="#8CC152"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        name="Potato"
                        connectNulls
                      />
                      <Line
                        type="linear"
                        dataKey="cvStone"
                        stroke="#E9573F"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                        name="Stones"
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  <PanelTitle style={{ marginTop: 16 }}>
                    Size histogram (per-interval detections, summed)
                  </PanelTitle>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart
                      data={cameraHistData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2e37" />
                      <XAxis
                        dataKey="bucket"
                        stroke="#8e9ba8"
                        tick={{ fill: "#8e9ba8", fontSize: 11 }}
                      />
                      <YAxis
                        stroke="#8e9ba8"
                        tick={{ fill: "#8e9ba8", fontSize: 11 }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v: number) => [v, "Count"]}
                      />
                      <Bar dataKey="count" fill="#8CC152" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              )}

              <Panel>
                <PanelTitle>Stats (visible range)</PanelTitle>
                <StatsTable>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Min</th>
                      <th>Max</th>
                      <th>Mean</th>
                      <th>Last</th>
                      <th>Std dev</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsRows.map((r) => (
                      <tr key={r.name}>
                        <td>
                          <ToggleSwatch color={r.color} /> {r.name}
                        </td>
                        <td>{r.min}</td>
                        <td>{r.max}</td>
                        <td>{r.mean}</td>
                        <td>{r.last}</td>
                        <td>{r.std}</td>
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </Panel>
            </>
          )}
        </MetricsShell>
      )}
    </div>
  );
};

export default DeviceMetricsSubTab;
